import type { CalculationInput, FeeProfile } from "@netkeep/fee-types";
import { calculate } from "./calculate";
import { D, type Dec, currencyDecimals, moneyString, parseSigned } from "./money";

/** Search ceiling: 1 billion major units. Beyond this a target is treated as unreachable. */
const MAX_PRICE = new D("1000000000");

/**
 * Finds the lowest sale price (in whole minor units) whose net profit is at
 * least `targetProfit`, holding every other input constant.
 *
 * Fees can be tiered, thresholded, capped, and rounded, so there is no single
 * closed-form inverse. Instead the real engine is evaluated with an
 * exponential search for an upper bound followed by bisection on integer
 * minor units, which needs ~2·log2(price in cents) evaluations.
 *
 * Profit is non-decreasing in price for any realistic fee schedule (marginal
 * fee rate below 100%). Small threshold steps in fixed fees can introduce
 * local dips of a few cents; the solver then returns a valid price at which
 * the target is met and one minor unit lower is not.
 *
 * Returns null when the target cannot be reached (e.g. combined percentage
 * fees ≥ 100%).
 */
export function solveSalePrice(
  input: CalculationInput,
  profile: FeeProfile | null,
  targetProfit: string,
): string | null {
  const currency = input.currency.toUpperCase();
  const target = parseSigned(targetProfit, "targetProfit");
  const unit = new D(1).dividedBy(new D(10).pow(currencyDecimals(currency)));

  const meetsTarget = (units: Dec): boolean => {
    const price = units.times(unit);
    const result = calculate({ ...input, salePrice: price.toFixed() }, profile, { includeBreakEven: false });
    return new D(result.netProfit).greaterThanOrEqualTo(target);
  };

  let low = new D(0);
  if (meetsTarget(low)) return moneyString(low, currency);

  const maxUnits = MAX_PRICE.dividedBy(unit);
  let high = new D(100);
  while (!meetsTarget(high)) {
    low = high;
    high = high.times(2);
    if (high.greaterThan(maxUnits)) return null;
  }

  // Invariant: meetsTarget(low) is false, meetsTarget(high) is true.
  while (high.minus(low).greaterThan(1)) {
    const mid = low.plus(high).dividedToIntegerBy(2);
    if (meetsTarget(mid)) high = mid;
    else low = mid;
  }
  return moneyString(high.times(unit), currency);
}
